import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { mediaUrl } from "@/lib/api";
import { captureProof } from "@/lib/proof";
import ApparelCanvas, { VIEW_LABELS } from "@/components/shirts/ApparelCanvas";
import { useShirtConfig } from "@/components/shirts/useShirtConfig";
import { useSimpleLine } from "./useSimpleLine";
import { FileCheck, NoteField, PriceBar, ReadyBy, Section, btnChip, dpiCheck, input, money } from "./Fields";

const STAGE_BG = "#e3e6ea";

function Swatches({ colors, value, onPick }) {
    if (colors.length < 2) return null;
    return (
        <div className="flex flex-wrap gap-2" data-testid="color-swatches">
            {colors.map((c, i) => (
                <button key={c.slug} type="button" onClick={() => onPick(i)} aria-label={c.color.name} title={c.color.name}
                    className={`h-11 w-11 border ${i === value ? "border-2 border-accent-brand" : "border-ink"}`}
                    style={{ background: c.color.hex }} data-testid={`swatch-${c.slug}`} />
            ))}
        </div>
    );
}

function SizeGrid({ product, sizes, setSize }) {
    const groups = product.size_groups || [];
    const [group, setGroup] = useState(groups[0]?.name);
    const g = groups.find((x) => x.name === group) || groups[0];
    return (
        <div data-testid="size-grid">
            {groups.length > 1 && (
                <div className="mb-3 flex gap-2">
                    {groups.map((gr) => <button key={gr.name} type="button" onClick={() => setGroup(gr.name)} className={btnChip(gr.name === g?.name)} data-testid={`size-group-${gr.name}`}>{gr.name}</button>)}
                </div>
            )}
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {(g?.sizes || []).map((s) => {
                    const key = `${g.name}|${s.label}`;
                    const val = Number(sizes?.[key]) || 0;
                    return (
                        <label key={key} className={`flex items-center justify-between gap-2 border px-2 py-1 ${val ? "border-2 border-accent-brand" : "border-ink"}`} data-testid={`size-row-${key}`}>
                            <span className="text-[16px]">{s.label}{s.upcharge > 0 && <span className="block text-[14px] text-muted-ink">+{money(s.upcharge)}</span>}</span>
                            <input type="number" inputMode="numeric" min={0} value={val || ""} placeholder="0" aria-label={`${s.label} quantity`}
                                onChange={(e) => setSize(key, Math.max(0, Number(e.target.value) || 0))}
                                className="min-h-[44px] w-16 border border-ink bg-field text-center text-[16px]" data-testid={`size-qty-${key}`} />
                        </label>
                    );
                })}
            </div>
        </div>
    );
}

function Supplied({ line }) {
    return (
        <Section testid="supplied-section">
            <label className="flex min-h-[44px] items-center gap-3 text-[16px]">
                <input type="checkbox" checked={line.supplied} onChange={(e) => { line.setSupplied(e.target.checked); line.setAck(false); }} className="h-6 w-6" data-testid="supplied-switch" />
                I'll bring my own shirts
            </label>
            {line.supplied && (
                <label className="mt-2 flex min-h-[44px] items-center gap-3 text-[16px]">
                    <input type="checkbox" checked={line.ack} onChange={(e) => line.setAck(e.target.checked)} className="h-6 w-6" data-testid="supplied-ack" />
                    Customer-supplied apparel is not replaced if damaged.
                </label>
            )}
        </Section>
    );
}

function TextTool({ cfg, selText, setSelText }) {
    const t = cfg.viewTexts[selText];
    return (
        <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="text-tool">
            <button type="button" onClick={() => setSelText(cfg.addText())} className={btnChip(false)} data-testid="add-text">Add text</button>
            {t && (
                <>
                    <input value={t.text} onChange={(e) => cfg.setText(selText, { ...t, text: e.target.value })} className={`${input} flex-1 min-w-[140px]`} aria-label="Text" data-testid="text-value" />
                    {["#ffffff", "#1a1a1a"].map((c) => (
                        <button key={c} type="button" aria-label={c === "#ffffff" ? "White ink" : "Black ink"} onClick={() => cfg.setText(selText, { ...t, color: c })}
                            className={`h-11 w-11 border ${t.color === c ? "border-2 border-accent-brand" : "border-ink"}`} style={{ background: c }} data-testid={`text-ink-${c.slice(1)}`} />
                    ))}
                    <button type="button" onClick={() => { cfg.removeText(selText); setSelText(null); }} className="min-h-[44px] underline" data-testid="remove-text">Remove</button>
                </>
            )}
        </div>
    );
}

/** T-Shirts & Hoodies / Vests: swatches, inline print-placement designer, size grid (5.3). */
export default function ShirtBody({ chip, productsBySlug, store, cleanupFee, placeholder, ready, allowSupplied }) {
    const colors = chip.products;
    const [ci, setCi] = useState(0);
    const product = productsBySlug[colors[Math.min(ci, colors.length - 1)]?.slug];
    const cfg = useShirtConfig(product, { store: store?.slug || product?.store_slug });
    const line = useSimpleLine({ store, cleanupFee });
    const [designing, setDesigning] = useState(false);
    const [selText, setSelText] = useState(null);
    const stageRef = useRef(null);
    const fileRef = useRef(null);
    // what the customer had before a color or type switch — read during render, so it is still the old garment's state
    const prev = useRef(null);
    prev.current = { arts: cfg.arts, sizes: cfg.sizes };

    useEffect(() => { setCi(0); }, [chip.id]);
    // a new color or type is a new product doc: carry the art and the sizes it also comes in across the reset
    useEffect(() => {
        const p = prev.current;
        if (!p || !product) return;
        const valid = new Set((product.size_groups || []).flatMap((g) => (g.sizes || []).map((s) => `${g.name}|${s.label}`)));
        Object.entries(p.arts).forEach(([v, a]) => a && cfg.setArt(v, a));
        Object.entries(p.sizes).forEach(([k, v]) => v && valid.has(k) && cfg.setSize(k, v));
    }, [product?.slug]); // eslint-disable-line react-hooks/exhaustive-deps

    const art = cfg.arts[cfg.view];
    const check = useMemo(() => dpiCheck(art?.file, art?.widthIn), [art?.file, art?.widthIn]);
    useEffect(() => { if (check && !check.ok) line.setCleanup(true); if (check?.ok) line.setCleanup(false); }, [check?.ok]); // eslint-disable-line react-hooks/exhaustive-deps

    const upload = async (file) => {
        try { await cfg.pickArt(cfg.view, file); } catch { toast.error("Upload failed. Try again."); }
    };

    const q = cfg.quote;
    const errors = [...(q?.errors || []), line.suppliedError].filter(Boolean);
    const total = (q?.line_total || 0) + line.fee;
    const build = async () => {
        const proof = await captureProof(stageRef.current, `${product.slug}-${cfg.view}`, STAGE_BG);
        return {
            product_slug: product.slug,
            product_name: product.name,
            image: proof ? mediaUrl(proof.url) : mediaUrl((product.mockups?.front || {}).url),
            quantity: q.quantity, unit_price: q.unit_price, line_total: q.line_total,
            summary: [`${q.quantity} pcs`, q.locations.join(" + "), ...q.lines.map((l) => `${l.label} ×${l.quantity}`)].filter(Boolean).join(" · "),
            store,
            selections: {
                kind: "shirt", category: product.category, store_slug: store?.slug || product.store_slug, turnaround: product.turnaround, ready_in: product.ready_in,
                color: cfg.color, sizes: cfg.sizes, locations: cfg.locations,
                art: Object.fromEntries(cfg.locations.filter((v) => cfg.arts[v]?.file).map((v) => [v, {
                    file: cfg.arts[v].file, width_in: cfg.arts[v].widthIn,
                    height_in: Number((cfg.arts[v].widthIn / cfg.arts[v].ratio).toFixed(2)), offset: { x: cfg.arts[v].x, y: cfg.arts[v].y },
                }])),
                texts: Object.fromEntries(Object.entries(cfg.texts).filter(([, l]) => (l || []).length)),
                design_json: { arts: cfg.arts, texts: cfg.texts, color: cfg.color },
            },
            proof, artwork: cfg.locations.map((v) => cfg.arts[v]?.file).filter(Boolean),
        };
    };

    if (!product) return null;
    return (
        <>
            <Section testid="design-section">
                <Swatches colors={colors} value={ci} onPick={setCi} />
                <div ref={stageRef} className="relative mt-3 h-[75vw] max-h-[480px] w-full border border-line" style={{ background: STAGE_BG }} data-testid="shirt-stage">
                    <ApparelCanvas product={product} view={cfg.view} art={art} onArtChange={(next) => cfg.setArt(cfg.view, next)} color={cfg.color}
                        texts={cfg.viewTexts} onTextChange={cfg.setText} selectedText={selText} onSelectText={setSelText} />
                    {!art?.url && (
                        <button type="button" onClick={() => fileRef.current?.click()} className="absolute inset-x-6 bottom-6 flex min-h-[48px] items-center justify-center border border-ink bg-page text-[16px] font-semibold" data-testid="upload-file">
                            {cfg.busy[cfg.view] ? "Uploading…" : `Upload file${cfg.view !== "front" ? ` · ${VIEW_LABELS[cfg.view]}` : ""}`}
                        </button>
                    )}
                </div>
                <input ref={fileRef} type="file" accept="image/*,.pdf,.ai,.eps,.svg" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid="upload-input" />
                <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-[16px]">
                    <button type="button" onClick={() => setDesigning((v) => !v)} className="min-h-[44px] underline" data-testid="design-it">{designing ? "Close designer" : "Design it"}</button>
                    {art?.url && <button type="button" onClick={() => fileRef.current?.click()} className="min-h-[44px] underline" data-testid="replace-file">Replace file</button>}
                </div>
                {designing && (
                    <div className="mt-2" data-testid="inline-designer">
                        <div className="flex flex-wrap gap-2" data-testid="view-tabs">
                            {cfg.views.map((v) => <button key={v} type="button" onClick={() => cfg.setView(v)} className={btnChip(cfg.view === v)} data-testid={`view-tab-${v}`}>{VIEW_LABELS[v]}{(cfg.arts[v]?.file || (cfg.texts[v] || []).length) ? " •" : ""}</button>)}
                        </div>
                        <TextTool cfg={cfg} selText={selText} setSelText={setSelText} />
                        <p className="mt-2 text-[14px] text-muted-ink">Drag to place. Each extra side adds {money(product.pricing?.extra_location_fee ?? 3)} a piece.</p>
                    </div>
                )}
                <FileCheck check={check} cleanup={line.cleanup} setCleanup={line.setCleanup} fee={cleanupFee} />
            </Section>
            <Section title="Sizes" testid="size-section">
                <SizeGrid product={product} sizes={cfg.sizes} setSize={cfg.setSize} />
            </Section>
            {allowSupplied && <Supplied line={line} />}
            <NoteField value={line.note} onChange={line.setNote} placeholder={product.note_placeholder || placeholder} />
            <ReadyBy store={store} readyIn={product.ready_in} extraDays={line.extraDays} fallback={ready} needBy={line.needBy} setNeedBy={line.setNeedBy} />
            <PriceBar total={total} busy={line.busy || cfg.pricing} errors={errors}
                note={q?.quantity ? `${q.quantity} pcs · ${money(q.unit_price)} each${line.fee ? ` + ${money(line.fee)} cleanup` : ""}` : "Pick your sizes"}
                onPay={() => line.submit(build, "pay")} onAdd={() => line.submit(build, "cart")} />
        </>
    );
}
